import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { Edit } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Link, Stack, Typography } from '@mui/material';

import { SavedRouteAlert } from 'state/emailAlertsSlices';

import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { displayFriendlyDayMonthYear } from 'features/common/datetimeUtils';
import { TaskBarTabPanel } from 'features/task_bar/TaskBarTabs';
import { secondary_light_text_color } from 'theme/cemTheme';

import { NotificationsPanel } from './NotificationsPanel';
import { AlertTab } from './alertConstants';
import { AlertTriggersField } from './alertFields/AlertTriggersField';
import { ManageRoutes } from './alertFields/ManageRoutes';
import { Recipients } from './alertFields/Recipients';
import { RouteMapDisplay } from './alertFields/RouteMapDisplay';
import { formatAlertName } from './alertHelpers';
import { AlertTabs } from './components/AlertTabs';
import { DeleteAlertPopup } from './components/DeleteAlertPopup';
import { useNotificationProject } from './useNotificationProject';

import styles from './Alert.module.css';

export { AlertTab, TABS } from './alertConstants';

interface AlertViewProps {
  alert: SavedRouteAlert;
  onEdit: () => void;
  onDelete: () => void;
}

export function AlertView({ alert, onEdit, onDelete }: AlertViewProps) {
  const [activeTab, setActiveTab] = useState<AlertTab>(AlertTab.Triggers);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const { urls } = useNotificationProject();

  const alertName = useMemo(
    () => formatAlertName(alert.metric_type, alert.metric_criteria),
    [alert.metric_type, alert.metric_criteria],
  );

  const routeIds = alert.routes.map((route) => route.id);
  const routeNames = alert.routes.map((route) => route.name);

  const handleTabChange = (tab: AlertTab) => {
    setActiveTab(tab);
  };

  const handleDeleteClick = () => {
    setIsDeletePopupOpen(true);
  };

  const handleDeleteConfirm = () => {
    onDelete();
    setIsDeletePopupOpen(false);
  };

  const handleDeleteCancel = () => {
    setIsDeletePopupOpen(false);
  };

  return (
    <NotificationsPanel>
      <Stack className={styles.header}>
        <Box className={styles.breadcrumbs}>
          <Link
            component={RouterLink}
            to={urls?.manage ?? '#'}
            className={styles.breadcrumbLink}
          >
            <Typography variant="body1">Manage Alerts</Typography>
          </Link>
          <FontAwesomeIcon
            name="chevron-right"
            sx={{ color: secondary_light_text_color }}
          />
          <Box className={styles.chipTitle}>
            <Typography variant="body1">{alertName}</Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            startIcon={<Edit className={styles.buttonIcon} />}
            onClick={onEdit}
          >
            <Typography variant="h4" className={styles.whiteButtonText}>
              Edit
            </Typography>
          </Button>
          <Button
            variant="text"
            startIcon={<DeleteIcon className={styles.buttonIcon} />}
            onClick={handleDeleteClick}
          >
            <Typography variant="h4" className={styles.whiteButtonText}>
              Delete
            </Typography>
          </Button>
        </Stack>
      </Stack>

      {alert.updated_by && (
        <Typography variant="caption" className={styles.alertCaption}>
          Last updated by {alert.updated_by} on{' '}
          {displayFriendlyDayMonthYear(alert.updated_on)}
        </Typography>
      )}
      <AlertTabs activeTab={activeTab} onTabChange={handleTabChange}>
        <TaskBarTabPanel value={activeTab} index={AlertTab.Triggers}>
          <AlertTriggersField
            mode="view"
            metricType={alert.metric_type}
            metricCriteria={alert.metric_criteria}
            typicalYearNum={alert.typical_year_num}
            typicalMonthNum={alert.typical_month_num}
            scheduleDayTypes={alert.schedule_day_types}
            scheduleDayHours={alert.schedule_day_hours}
            hasResolutionEmail={alert.has_resolution_email}
          />
        </TaskBarTabPanel>
        <TaskBarTabPanel
          value={activeTab}
          index={AlertTab.Routes}
          extraClass={styles.fullHeight}
        >
          <Stack direction="row" spacing={2} width="100%" height="100%">
            <Box flex={1}>
              <ManageRoutes mode="view" routes={routeNames} />
            </Box>
            <Box flex={1}>
              <RouteMapDisplay selectedRoutes={[]} availableRoutes={routeIds} />
            </Box>
          </Stack>
        </TaskBarTabPanel>
        <TaskBarTabPanel
          value={activeTab}
          index={AlertTab.Recipients}
          extraClass={styles.fullHeight}
        >
          <Recipients mode="view" recipients={alert.recipients} />
        </TaskBarTabPanel>
      </AlertTabs>

      <DeleteAlertPopup
        isOpen={isDeletePopupOpen}
        alertName={alertName}
        routes={routeNames}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </NotificationsPanel>
  );
}
